import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Edit, FolderPlus, Folder, ChevronRight, ChevronDown, Eye, Box } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from '@/components/Button';
import { DeleteButton } from '@/components/DeleteButton';
import { EmptyState } from '@/components/EmptyState';
import { useCollectionTree, useDeleteCollection } from '@/features/collections/hooks';
import { CollectionFilters, CollectionTable } from '@/features/collections/components';
import type { CollectionViewMode } from '@/features/collections/components/CollectionFilters';
import { useUIStore } from '@/stores/ui';
import type { Collection } from '@maker-toolchest/shared';
import { MAX_COLLECTION_DEPTH } from '@maker-toolchest/shared';

// Get icon component from icon name string
function getIconComponent(iconName?: string | null): React.ComponentType<any> {
  if (!iconName) return Folder; // Default icon

  // Convert kebab-case to PascalCase (e.g., "folder-open" -> "FolderOpen")
  const pascalCase = iconName
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');

  const LucideIcon = (LucideIcons as any)[pascalCase];
  if (LucideIcon) {
    return LucideIcon;
  }

  // Fallback to default
  return Folder;
}

// Flatten tree into array for table view
function flattenCollectionTree(tree: any[]): { collections: Collection[], parentMap: Map<string, string> } {
  const flattened: Collection[] = [];
  const parentMap = new Map<string, string>(); // parentId -> parentName

  function flatten(collections: any[]) {
    for (const collection of collections) {
      flattened.push(collection);
      // Add parent mapping for all children
      if (collection.children && collection.children.length > 0) {
        for (const child of collection.children) {
          parentMap.set(child.id, collection.name);
        }
        flatten(collection.children);
      }
    }
  }

  flatten(tree);
  return { collections: flattened, parentMap };
}

// Filter collections by search
function filterCollections(collections: Collection[], search: string): Collection[] {
  if (!search) return collections;

  const searchLower = search.toLowerCase();
  return collections.filter(c =>
    c.name.toLowerCase().includes(searchLower) ||
    (c.description && c.description.toLowerCase().includes(searchLower))
  );
}

export function CollectionsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Read filters from URL
  const search = searchParams.get('search') || '';
  const viewMode = (searchParams.get('view') as CollectionViewMode) || 'tree';

  const { data: collectionTree, isLoading } = useCollectionTree();
  const deleteCollection = useDeleteCollection();
  const { addToast, setCollectionsFilterOpen, openCollectionSidebar, autoExpandCollectionId, setAutoExpandCollectionId } = useUIStore();

  // Helper to update a single filter in URL
  const updateFilter = (key: string, value: string | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  // Auto-expand first 2 layers when collection tree loads
  useEffect(() => {
    if (collectionTree && collectionTree.length > 0) {
      const idsToExpand = new Set<string>();

      // Recursively collect IDs of collections at depth 0 and 1
      const collectExpandableIds = (collections: any[], currentDepth: number) => {
        for (const collection of collections) {
          if (currentDepth < 2) {
            idsToExpand.add(collection.id);
            if (collection.children && collection.children.length > 0) {
              collectExpandableIds(collection.children, currentDepth + 1);
            }
          }
        }
      };

      collectExpandableIds(collectionTree, 0);
      setExpandedIds(idsToExpand);
    }
  }, [collectionTree]);

  // Auto-expand parent when a sub-collection is created
  useEffect(() => {
    if (autoExpandCollectionId) {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        next.add(autoExpandCollectionId);
        return next;
      });
      // Clear the flag after expanding
      setAutoExpandCollectionId(null);
    }
  }, [autoExpandCollectionId, setAutoExpandCollectionId]);

  const toggleExpanded = (collectionId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(collectionId)) {
        next.delete(collectionId);
      } else {
        next.add(collectionId);
      }
      return next;
    });
  };

  const handleViewModels = (collectionId: string) => {
    // Navigate to models page with collection filter in URL
    setCollectionsFilterOpen(true);
    navigate(`/models?collection=${collectionId}`);
  };

  const handleDelete = async (collection: Collection) => {
    if (!confirm(`Are you sure you want to delete "${collection.name}"?`)) {
      return;
    }

    setDeletingId(collection.id);
    try {
      await deleteCollection.mutateAsync(collection.id);
      addToast({
        type: 'success',
        title: 'Collection deleted',
        message: `"${collection.name}" has been deleted`,
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Failed to delete collection',
        message: error instanceof Error ? error.message : 'An error occurred',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const renderCollectionRow = (collection: any, level = 0) => {
    const isDeleting = deletingId === collection.id;
    const IconComponent = getIconComponent(collection.icon);
    const hasChildren = collection.children && collection.children.length > 0;
    const isExpanded = expandedIds.has(collection.id);
    const isAtMaxDepth = collection.depth >= MAX_COLLECTION_DEPTH;

    // Calculate total items recursively
    const getTotalItems = (col: any): number => {
      let total = col.modelCount || 0;
      if (col.children) {
        for (const child of col.children) {
          total += getTotalItems(child);
        }
      }
      return total;
    };

    const directItems = collection.modelCount || 0;
    const directSubCollections = collection.children?.length || 0;
    const totalItems = getTotalItems(collection);
    const showTotalItems = totalItems !== directItems;

    // Build stats string
    const statsParts = [];
    statsParts.push(`${directItems} ${directItems === 1 ? 'Item' : 'Items'}`);
    if (directSubCollections > 0) {
      statsParts.push(`${directSubCollections} ${directSubCollections === 1 ? 'Sub-Collection' : 'Sub-Collections'}`);
    }
    if (showTotalItems) {
      statsParts.push(`${totalItems} Total Items`);
    }
    const statsText = statsParts.join(' | ');

    return (
      <div key={collection.id}>
        {/* Collection Row */}
        <div
          className="group flex items-center gap-2 py-2 px-3 hover:bg-background-subtle/50 transition-colors cursor-pointer"
          style={{ paddingLeft: `${level * 1.5 + 0.75}rem` }}
          onClick={() => hasChildren && toggleExpanded(collection.id)}
        >
          {/* Expand/Collapse Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded(collection.id);
            }}
            className="p-0.5 hover:bg-background-subtle rounded transition-colors flex-shrink-0"
            style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-foreground-muted" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-foreground-muted" />
            )}
          </button>

          {/* Icon */}
          <IconComponent
            className="h-4 w-4 flex-shrink-0"
            style={{ color: collection.color || '#6b7280' }}
          />

          {/* Name & Description & Stats */}
          <div className="flex-1 min-w-0 py-0.5">
            <div className="flex items-baseline gap-2 min-w-0">
              <h3
                className="text-sm font-medium text-foreground truncate flex-shrink hover:text-primary transition-colors cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/collections/${collection.id}`);
                }}
              >
                {collection.name}
              </h3>
              {collection.description && (
                <span className="text-xs text-foreground-muted/60 truncate flex-shrink">
                  {collection.description}
                </span>
              )}
            </div>
            <p className="text-xs text-foreground-muted/70 tabular-nums mt-0.5">
              {statsText}
            </p>
          </div>

          {/* Actions - Hidden until hover */}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/collections/${collection.id}`);
              }}
              className="p-1.5 hover:bg-background-subtle rounded transition-colors"
              title="View details"
            >
              <Eye className="h-3.5 w-3.5 text-foreground-muted" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleViewModels(collection.id);
              }}
              className="p-1.5 hover:bg-background-subtle rounded transition-colors"
              title="View models"
            >
              <Box className="h-3.5 w-3.5 text-foreground-muted" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (isAtMaxDepth) return;
                openCollectionSidebar({
                  defaultParentId: collection.id,
                  parentCollectionName: collection.name,
                });
              }}
              disabled={isAtMaxDepth}
              className={`p-1.5 rounded transition-colors ${
                isAtMaxDepth
                  ? 'opacity-40 cursor-not-allowed'
                  : 'hover:bg-background-subtle'
              }`}
              title={isAtMaxDepth ? 'Maximum nesting depth reached' : 'Add sub-collection'}
            >
              <FolderPlus className="h-3.5 w-3.5 text-foreground-muted" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                openCollectionSidebar({ collection });
              }}
              className="p-1.5 hover:bg-background-subtle rounded transition-colors"
              title="Edit collection"
            >
              <Edit className="h-3.5 w-3.5 text-foreground-muted" />
            </button>
            <div
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <DeleteButton
                onClick={() => handleDelete(collection)}
                disabled={isDeleting}
                showText={false}
                variant="ghost"
                title="Delete collection"
              />
            </div>
          </div>
        </div>

        {/* Children - Only render when expanded */}
        {hasChildren && isExpanded &&
          collection.children.map((child: any) => renderCollectionRow(child, level + 1))}
      </div>
    );
  };

  // Prepare collections for current view mode
  const flattenedResult = collectionTree ? flattenCollectionTree(collectionTree) : { collections: [], parentMap: new Map() };
  const allCollections = flattenedResult.collections;
  const parentNameMap = flattenedResult.parentMap;
  const filteredCollections = filterCollections(allCollections, search);
  const hasFilters = search;

  return (
    <div className="p-4">
      {/* Page header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-foreground">Collections</h1>
          <p className="text-sm text-foreground-muted">Organize your 3D models into collections</p>
        </div>
        <Button size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={() => openCollectionSidebar()}>
          New Collection
        </Button>
      </div>

      {/* Filters */}
      <div className="mb-4">
        <CollectionFilters
          search={search}
          viewMode={viewMode}
          onSearchChange={(value) => updateFilter('search', value || null)}
          onViewModeChange={(mode) => updateFilter('view', mode)}
        />
      </div>

      {/* Collections List */}
      {isLoading ? (
        <div className="rounded border border-border bg-background-subtle p-4">
          <div className="text-center text-foreground-muted">Loading collections...</div>
        </div>
      ) : collectionTree && collectionTree.length > 0 ? (
        viewMode === 'table' ? (
          filteredCollections.length === 0 ? (
            <div className="rounded border border-border bg-background-subtle p-4">
              <EmptyState
                icon={<Folder className="h-10 w-10" />}
                title={hasFilters ? "No collections found" : "No collections yet"}
                description={
                  hasFilters
                    ? "No collections match your search criteria."
                    : "Create your first collection to start organizing your 3D models"
                }
                actionLabel={hasFilters ? undefined : "New Collection"}
                onAction={hasFilters ? undefined : () => openCollectionSidebar()}
              />
            </div>
          ) : (
            <CollectionTable
              collections={filteredCollections}
              parentNameMap={parentNameMap}
              onDelete={handleDelete}
              deletingId={deletingId}
            />
          )
        ) : (
          <div className="border border-border rounded-lg overflow-hidden">
            {collectionTree.map((collection) => renderCollectionRow(collection))}
          </div>
        )
      ) : (
        <div className="rounded border border-border bg-background-subtle p-4">
          <EmptyState
            icon={<Folder className="h-10 w-10" />}
            title="No collections yet"
            description="Create your first collection to start organizing your 3D models"
            actionLabel="New Collection"
            onAction={() => openCollectionSidebar()}
          />
        </div>
      )}
    </div>
  );
}
